import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  IconButton,
  Typography,
  TextField,
  Paper,
  Fade,
  Chip,
  CircularProgress,
  Tooltip
} from '@mui/material';
import {
  Close as CloseIcon,
  Send as SendIcon,
  Security as SecurityIcon,
  AutoAwesome as SparkleIcon,
  SmartToy as BotIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';

const STARTER_PROMPTS = [
  'What services does Verve AI Labs offer?',
  'How does the GST Compliance Portal work?',
  'Can Verve AI build custom AI agents for us?',
  'How do I contact sales or support?'
];

// In production, uses /api/chat rewrite; in local dev, connects directly to Cloud Run / Cloud Function
const CHAT_ENDPOINT =
  (typeof import.meta !== 'undefined' && import.meta.env && import.meta.env.VITE_CHAT_API_URL) ||
  (typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')
    ? 'https://us-central1-verveailabs.cloudfunctions.net/verveChatAgent'
    : '/api/chat');

export default function VerveChatWidget() {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      role: 'model',
      text: 'Hello! I am the Verve AI Concierge. How can I help you today with Verve AI Labs, our enterprise solutions, or GST compliance?',
      timestamp: new Date()
    }
  ]);
  const [inputText, setInputText] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [securityStatus, setSecurityStatus] = useState('Protected');
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    if (isOpen) {
      scrollToBottom();
    }
  }, [messages, isOpen]);

  const handleSendMessage = async (textToSend) => {
    const query = (textToSend || inputText).trim();
    if (!query || isLoading) return;

    const userMessage = {
      role: 'user',
      text: query,
      timestamp: new Date()
    };

    const newHistory = [...messages, userMessage];
    setMessages(newHistory);
    setInputText('');
    setIsLoading(true);

    try {
      const formattedHistory = newHistory
        .filter(m => m.role === 'user' || m.role === 'model')
        .map(m => ({ role: m.role, text: m.text }));

      const res = await fetch(CHAT_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: query,
          history: formattedHistory.slice(-6)
        })
      });

      if (!res.ok) {
        throw new Error(`HTTP ${res.status}`);
      }

      const data = await res.json();
      
      if (data.filtered) {
        setMessages(prev => [
          ...prev,
          {
            role: 'model',
            text: data.response || 'Your query was filtered by Model Armor safety guardrails.',
            isFiltered: true,
            timestamp: new Date()
          }
        ]);
        setSecurityStatus('Shield Triggered');
      } else {
        setMessages(prev => [
          ...prev,
          {
            role: 'model',
            text: data.response || 'Thank you for reaching out. Please contact support@verveailabs.com for more details.',
            timestamp: new Date()
          }
        ]);
        setSecurityStatus('Armor Verified');
      }
    } catch (err) {
      console.error('[VerveChat] Communication error:', err);
      setMessages(prev => [
        ...prev,
        {
          role: 'model',
          text: 'I am currently unable to reach the knowledge service. Please try again in a few moments or email us at support@verveailabs.com.',
          isError: true,
          timestamp: new Date()
        }
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const resetChat = () => {
    setMessages([
      {
        role: 'model',
        text: 'Hello! I am the Verve AI Concierge. How can I help you today with Verve AI Labs, our enterprise solutions, or GST compliance?',
        timestamp: new Date()
      }
    ]);
  };

  return (
    <Box
      sx={{
        position: 'fixed',
        bottom: 24,
        right: 24,
        zIndex: 99999,
        fontFamily: 'Inter, system-ui, sans-serif'
      }}
    >
      {/* Floating Toggle Button */}
      {!isOpen && (
        <Tooltip title="Chat with Verve AI Concierge" placement="left" arrow>
          <Box
            onClick={() => setIsOpen(true)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              px: 2.2,
              py: 1.4,
              borderRadius: '9999px',
              background: 'linear-gradient(135deg, #0b0c0e 0%, #131418 100%)',
              border: '1px solid rgba(6, 182, 212, 0.4)',
              boxShadow: '0 8px 32px rgba(6, 182, 212, 0.25)',
              cursor: 'pointer',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              '&:hover': {
                transform: 'translateY(-3px)',
                borderColor: '#06b6d4',
                boxShadow: '0 12px 36px rgba(6, 182, 212, 0.4)'
              }
            }}
          >
            <Box
              sx={{
                width: 10,
                height: 10,
                borderRadius: '50%',
                bgcolor: '#10b981',
                boxShadow: '0 0 10px #10b981',
                animation: 'pulse 2s infinite'
              }}
            />
            <SparkleIcon sx={{ color: '#06b6d4', fontSize: 20 }} />
            <Typography
              sx={{
                color: '#f8fafc',
                fontWeight: 600,
                fontSize: '0.925rem',
                letterSpacing: '0.01em'
              }}
            >
              Ask Verve AI
            </Typography>
          </Box>
        </Tooltip>
      )}

      {/* Expanded Chat Drawer */}
      <Fade in={isOpen}>
        <Paper
          elevation={24}
          sx={{
            display: isOpen ? 'flex' : 'none',
            flexDirection: 'column',
            width: { xs: 'calc(100vw - 32px)', sm: 400 },
            height: { xs: '80vh', sm: 580 },
            maxHeight: 680,
            borderRadius: '16px',
            background: '#0b0c0e',
            border: '1px solid #262930',
            boxShadow: '0 20px 50px rgba(0, 0, 0, 0.8), 0 0 30px rgba(6, 182, 212, 0.15)',
            overflow: 'hidden'
          }}
        >
          {/* Header */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              px: 2.5,
              py: 2,
              borderBottom: '1px solid #1f2228',
              background: 'linear-gradient(180deg, #131418 0%, #0b0c0e 100%)'
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: '10px',
                  background: 'linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 0 15px rgba(6, 182, 212, 0.4)'
                }}
              >
                <BotIcon sx={{ color: '#ffffff', fontSize: 22 }} />
              </Box>
              <Box>
                <Typography sx={{ color: '#f8fafc', fontWeight: 700, fontSize: '0.975rem' }}>
                  Verve AI Concierge
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
                  <Box
                    sx={{
                      width: 6,
                      height: 6,
                      borderRadius: '50%',
                      bgcolor: '#10b981'
                    }}
                  />
                  <Typography sx={{ color: '#94a3b8', fontSize: '0.75rem', fontWeight: 500 }}>
                    Enterprise Grounded
                  </Typography>
                </Box>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Tooltip title="Reset Chat">
                <IconButton onClick={resetChat} size="small" sx={{ color: '#64748b', '&:hover': { color: '#f8fafc' } }}>
                  <RefreshIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <IconButton onClick={() => setIsOpen(false)} size="small" sx={{ color: '#64748b', '&:hover': { color: '#f8fafc' } }}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>

          {/* Messages Container */}
          <Box
            sx={{
              flexGrow: 1,
              overflowY: 'auto',
              p: 2.2,
              display: 'flex',
              flexDirection: 'column',
              gap: 1.8,
              '&::-webkit-scrollbar': { width: '6px' },
              '&::-webkit-scrollbar-thumb': { bgcolor: '#262930', borderRadius: '4px' }
            }}
          >
            {messages.map((msg, idx) => (
              <Box
                key={idx}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: msg.role === 'user' ? 'flex-end' : 'flex-start'
                }}
              >
                <Box
                  sx={{
                    maxWidth: '85%',
                    p: 1.6,
                    borderRadius: msg.role === 'user' ? '14px 14px 2px 14px' : '14px 14px 14px 2px',
                    bgcolor: msg.role === 'user'
                      ? '#0891b2'
                      : msg.isFiltered
                      ? '#451a03'
                      : '#16181d',
                    border: msg.role === 'user'
                      ? 'none'
                      : msg.isFiltered
                      ? '1px solid #b45309'
                      : '1px solid #262930',
                    color: msg.role === 'user' ? '#ffffff' : '#f1f5f9',
                    fontSize: '0.885rem',
                    lineHeight: 1.55,
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    boxShadow: msg.role === 'user' ? '0 4px 16px rgba(6, 182, 212, 0.25)' : 'none'
                  }}
                >
                  {msg.text}
                </Box>
                <Typography sx={{ color: '#64748b', fontSize: '0.7rem', mt: 0.4, px: 0.5 }}>
                  {msg.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </Typography>
              </Box>
            ))}

            {isLoading && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2, p: 1.5, bgcolor: '#16181d', borderRadius: '12px', width: 'fit-content' }}>
                <CircularProgress size={16} sx={{ color: '#06b6d4' }} />
                <Typography sx={{ color: '#94a3b8', fontSize: '0.825rem' }}>
                  Consulting knowledge base...
                </Typography>
              </Box>
            )}

            <div ref={messagesEndRef} />
          </Box>

          {/* Quick Starter Suggestions */}
          {messages.length <= 2 && (
            <Box sx={{ px: 2, pb: 1, display: 'flex', flexWrap: 'wrap', gap: 0.8 }}>
              {STARTER_PROMPTS.map((prompt, i) => (
                <Chip
                  key={i}
                  label={prompt}
                  size="small"
                  onClick={() => handleSendMessage(prompt)}
                  sx={{
                    bgcolor: '#16181d',
                    color: '#06b6d4',
                    border: '1px solid #262930',
                    fontSize: '0.76rem',
                    cursor: 'pointer',
                    '&:hover': {
                      bgcolor: '#1f2228',
                      borderColor: '#06b6d4'
                    }
                  }}
                />
              ))}
            </Box>
          )}

          {/* Input Box */}
          <Box
            sx={{
              p: 1.8,
              borderTop: '1px solid #1f2228',
              background: '#0e0f12'
            }}
          >
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <TextField
                fullWidth
                size="small"
                placeholder="Ask about Verve AI Labs, products, GST..."
                value={inputText}
                onChange={(e) => setInputText(e.target.value)}
                onKeyDown={handleKeyDown}
                disabled={isLoading}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    bgcolor: '#16181d',
                    color: '#f8fafc',
                    borderRadius: '10px',
                    fontSize: '0.875rem',
                    '& fieldset': { borderColor: '#262930' },
                    '&:hover fieldset': { borderColor: '#3b3f4a' },
                    '&.Mui-focused fieldset': { borderColor: '#06b6d4' }
                  }
                }}
              />
              <IconButton
                onClick={() => handleSendMessage()}
                disabled={!inputText.trim() || isLoading}
                sx={{
                  bgcolor: '#06b6d4',
                  color: '#0b0c0e',
                  width: 40,
                  height: 40,
                  borderRadius: '10px',
                  '&:hover': { bgcolor: '#22d3ee' },
                  '&.Mui-disabled': { bgcolor: '#1f2228', color: '#4b5563' }
                }}
              >
                <SendIcon fontSize="small" />
              </IconButton>
            </Box>

            {/* Model Armor Shield Footer Badge */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 1.2, px: 0.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <SecurityIcon sx={{ color: '#10b981', fontSize: 14 }} />
                <Typography sx={{ color: '#64748b', fontSize: '0.72rem', fontWeight: 500 }}>
                  Shielded by Google Cloud Model Armor
                </Typography>
              </Box>
              <Typography sx={{ color: '#475569', fontSize: '0.7rem' }}>
                Gemini 3.8 Flash
              </Typography>
            </Box>
          </Box>
        </Paper>
      </Fade>
    </Box>
  );
}
